/**
 * @author Antoine Laviron
 * @date 2024-09-03
 *
 * @description
 * This component is a presentation page designed to showcase the application's
 * OAuth 2.0 PKCE authentication flow for Dreamforce. It breaks down the
 * flow into distinct, easy-to-follow steps, displaying the relevant
 * frontend (React) and backend (Python) code, along with concrete examples
 * of the data being exchanged.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';

// --- Code Snippets for Display ---

const step1FrontendCode = `
// File: /frontend/src/pages/Login.js

const handleLogin = async () => {
  // 1. Generate a verifier and a challenge for PKCE
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  
  // 2. Store the verifier in the session to use it after redirect
  sessionStorage.setItem('code_verifier', codeVerifier);

  // 3. Build the authorization URL with PKCE parameters
  const authUrl = \`\${config.salesforceUrl}/services/oauth2/authorize?
    response_type=code&
    client_id=\${config.clientId}&
    redirect_uri=\${encodeURIComponent(redirectUri)}&
    scope=web+lightning+api&
    code_challenge=\${codeChallenge}&
    code_challenge_method=S256\`;

  // 4. Redirect the user to Salesforce to log in
  window.location.href = authUrl;
};
`;

const step2FrontendCode = `
// File: /frontend/src/pages/Callback.js

useEffect(() => {
  const authenticate = async () => {
    // 1. Get the authorization code from the URL
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get('code');

    // 2. Retrieve the verifier we stored earlier
    const codeVerifier = sessionStorage.getItem('code_verifier');

    // 3. Send the code and verifier to our secure backend
    const response = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            code: authCode,
            code_verifier: codeVerifier,
            // ...other details
        }),
    });
    
    const data = await response.json();

    // 4. Store the secure frontdoor URL from the backend
    localStorage.setItem('frontdoor_url', data.frontdoor_url);
    
    // 5. Redirect to the main app
    navigate('/dashboard');
  };

  authenticate();
}, [navigate]);
`;

const step3BackendCode = `
# File: /backend/app/routes.py

@router.post("/api/auth")
async def authenticate(auth_request: AuthRequest):
    token_url = f"{settings.SALESFORCE_INSTANCE_URL}/services/oauth2/token"
    
    # 1. Prepare payload for Salesforce, including the SECRET
    params = {
        "grant_type": "authorization_code",
        "client_id": auth_request.client_id, 
        "client_secret": settings.SALESFORCE_CLIENT_SECRET,
        "code": auth_request.code,
        "code_verifier": auth_request.code_verifier
    }

    async with httpx.AsyncClient() as client:
        # 2. Exchange the code for an access token (server-to-server)
        token_response = await client.post(token_url, data=params)
        token_data = token_response.json()
        access_token = token_data.get("access_token")

        # 3. Use the access token to get a frontdoor URL
        frontdoor_service_url = f"{instance_url}/services/oauth2/singleaccess"
        headers = {'Authorization': f'Bearer {access_token}'}
        frontdoor_response = await client.post(frontdoor_service_url, headers=headers)
        frontdoor_data = frontdoor_response.json()

        # 4. Return the secure frontdoor URL to the frontend
        return {
            "frontdoor_url": frontdoor_data.get("frontdoor_uri")
        }
`;

function AuthFlow() {
    const navigate = useNavigate();

    const CodeBlock = ({ title, language, code }) => (
        <div className="code-block-container">
            <h4>{title}</h4>
            <pre className={`language-${language}`}>
                <code>{code.trim()}</code>
            </pre>
        </div>
    );

    const ExampleBlock = ({ title, examples }) => (
        <div className="example-block-container">
            <h4>{title}</h4>
            <ul>
                {examples.map((ex, index) => (
                    <li key={index}>
                        <strong>{ex.label}:</strong>
                        <span className="example-value">{ex.value}</span>
                    </li>
                ))}
            </ul>
        </div>
    );

    return (
        <div className="dashboard-page auth-flow-page">
            <header className="page-header">
                <h1>OAuth 2.0 PKCE Authentication Flow</h1>
                <button onClick={() => navigate(-1)} className="playground-back-button">
                    ← Back to App
                </button>
            </header>

            <div className="auth-flow-intro">
                <h2>The Core Idea: Protecting the Client Secret</h2>
                <p>
                    The entire goal of this multi-step process is to protect our application's most valuable credential: the <strong>Client Secret</strong>. It must <em>never</em> be exposed in the user's web browser. This is why the flow is carefully split between the insecure frontend (the browser) and our secure backend server.
                </p>
            </div>

            <div className="auth-flow-legend">
                <h4>Legend of Terms</h4>
                <ul>
                    <li><strong><code>code_verifier</code></strong> (The "Secret Password"): A temporary, random secret created by the frontend.</li>
                    <li><strong><code>code_challenge</code></strong> (The "Public Riddle"): A scrambled version of the Secret Password.</li>
                    <li><strong><code>authorization_code</code></strong> (The "Temporary Pass"): A one-time code from Salesforce after user login.</li>
                    <li><strong><code>client_secret</code></strong> (The "Master Key"): Our application's permanent secret, known only to the backend.</li>
                    <li><strong><code>access_token</code></strong> (The "Final Prize"): The powerful token our backend gets, which is never sent to the browser.</li>
                </ul>
            </div>


            <div className="auth-flow-steps">
                {/* --- Step 1 --- */}
                <div className="auth-flow-step">
                    <div className="step-header">
                        <span className="step-number">1</span>
                        <h2>Initiate Login: Create Challenge</h2>
                    </div>
                    <p className="step-description">
                        When the user clicks "Login," the frontend app creates a <code>code_verifier</code> and a <code>code_challenge</code>. It keeps the verifier hidden and sends the public challenge to Salesforce.
                    </p>
                    <CodeBlock title="Frontend Logic (React)" language="javascript" code={step1FrontendCode} />
                    <ExampleBlock title="Example Data Generated by Frontend" examples={[
                        { label: 'code_verifier', value: 'AbCdEfGhIjKlMnOpQrStUvWxYz0123456789AbCdEfGhI' },
                        { label: 'code_challenge', value: 'u3-A-g_... (Base64URL-encoded SHA256 hash of verifier)' }
                    ]} />
                </div>

                {/* --- Step 2 --- */}
                <div className="auth-flow-step">
                    <div className="step-header">
                        <span className="step-number">2</span>
                        <h2>Handle Callback: Get Auth Code</h2>
                    </div>
                    <p className="step-description">
                        After the user approves the login, Salesforce gives them an <code>authorization_code</code> and sends them back to our app. The frontend takes this code, retrieves the <code>code_verifier</code> it saved earlier, and sends both to our secure backend.
                    </p>
                    <CodeBlock title="Frontend Logic (React)" language="javascript" code={step2FrontendCode} />
                    <ExampleBlock title="Example Data Received & Sent" examples={[
                        { label: 'authorization_code', value: 'a29v... (a short-lived, one-time use code)' },
                        { label: 'code_verifier', value: 'AbCdEfGhIjKlMnOpQrStUvWxYz0123456789AbCdEfGhI' }
                    ]} />
                </div>

                {/* --- Step 3 --- */}
                <div className="auth-flow-step">
                    <div className="step-header">
                        <span className="step-number">3</span>
                        <h2>Secure Exchange: Use Client Secret</h2>
                    </div>
                     <p className="step-description">
                        This is the critical step. Our backend takes the auth code and verifier. It adds its own <code>client_secret</code>. Salesforce checks that the verifier matches the challenge from Step 1 and that the client secret is valid. If so, it grants an <code>access_token</code>, which our backend uses to create the secure <strong>Frontdoor URL</strong>.
                    </p>
                    <CodeBlock title="Backend Logic (Python/FastAPI)" language="python" code={step3BackendCode} />
                    <div className="security-warning">
                        <h4>⚠️ Token Security Warning</h4>
                        <p>The <code>access_token</code> is extremely powerful. It is handled entirely on the backend and is <strong>never</strong> exposed to the frontend. The <code>frontdoor_url</code> it generates contains a session token (<code>sid</code>) that grants cookie-less login, making this secure backend handling absolutely critical.</p>
                    </div>
                    <ExampleBlock title="Example Data Returned to Frontend" examples={[
                        { label: 'access_token', value: '00D... (a long-lived token, never exposed to browser)' },
                        { label: 'frontdoor_url', value: '/secur/frontdoor.jsp?sid=00D...&retURL=...' }
                    ]} />
                </div>
                
                {/* --- Summary --- */}
                <div className="auth-flow-summary">
                    <div className="step-header">
                         <span className="step-number">✅</span>
                        <h2>Flow Summary</h2>
                    </div>
                    <ul>
                        <li>Frontend creates a <code>code_verifier</code> + <code>code_challenge</code>.</li>
                        <li>User logs into Salesforce → returns an <code>authorization_code</code>.</li>
                        <li>Frontend sends the auth code + verifier to the backend.</li>
                        <li>Backend uses its <code>client_secret</code> + the other data to get an <code>access_token</code>.</li>
                        <li>Backend uses the access token to generate a secure <code>frontdoor_url</code> and sends it to the frontend.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default AuthFlow;

